<template>
  <div class="advanced-staff-select">
    <el-popover
      v-model:visible="visible"
      trigger="click"
      placement="bottom-start"
      :width="panelWidth"
      :offset="6"
      :show-arrow="false"
      popper-class="staff-select-popper"
      @show="handleShow"
      @hide="handleHide"
    >
      <template #reference>
        <el-input
          :style="{ width: width + 'px' }"
          :model-value="displayText"
          readonly
          clearable
          :placeholder="placeholder"
          @clear.stop="clear"
        >
          <template
            v-if="title"
            #prefix
          >
            {{ title }}：
          </template>
          <template #suffix>
            <el-icon><ArrowDown /></el-icon>
          </template>
        </el-input>
      </template>

      <div
        class="staff-select-panel"
        :style="{ width: panelWidth + 'px' }"
      >
        <div class="staff-tabs">
          <button
            v-for="tab in staffTabs"
            :key="tab.value"
            type="button"
            :class="['staff-tab', { active: tab.value === activeStaffType }]"
            @click="switchStaffType(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="staff-search-wrap">
          <el-input
            v-model="keyword"
            class="staff-search"
            clearable
            placeholder="员工账号/姓名/手机号/员工ID"
            @input="handleKeywordInput"
            @clear="searchOptions"
          />
        </div>

        <div
          v-scroll-bottom="loadNextPage"
          class="staff-list"
        >
          <div
            v-for="item in optionList"
            :key="item.id"
            :class="['staff-option', { selected: isSelected(item) }]"
            @click="toggleOption(item)"
          >
            <div class="staff-info">
              <div class="staff-name">
                {{ item.name }}
              </div>
            </div>
            <el-icon
              v-if="isSelected(item)"
              class="staff-check"
            >
              <Check />
            </el-icon>
          </div>

          <el-empty
            v-if="!loading && !optionList.length"
            :image-size="60"
            description="暂无数据"
          />
          <div
            v-if="loading"
            class="staff-state"
          >
            加载中...
          </div>
          <div
            v-else-if="optionList.length && noMore"
            class="staff-state"
          >
            没有更多了
          </div>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { ArrowDown, Check } from '@element-plus/icons-vue';
import request from '../../utils/request.ts';

interface StaffItem {
  id?: string | number;
  name?: string;
  userId?: string | number;
  userName?: string;
  nickName?: string;
  phonenumber?: string;
  staffType?: string;
  shopName?: string;
  raw?: any;
  [key: string]: any;
}

const props = withDefaults(
  defineProps<{
    modelValue?: StaffItem[] | StaffItem | Array<string | number> | string | number | null;
    title?: string;
    width?: number;
    panelWidth?: number;
    placeholder?: string;
    multiple?: boolean;
    pageSize?: number;
    idKey?: string;
    nameKey?: string;
    linkParams?: Record<string, any>;
  }>(),
  {
    modelValue: () => [],
    title: '',
    width: 230,
    panelWidth: 320,
    placeholder: '请选择',
    multiple: true,
    pageSize: 10,
    idKey: 'userId',
    nameKey: 'nickName',
    linkParams: () => ({}),
  },
);

const emits = defineEmits<{
  (e: 'update:modelValue', value: StaffItem[]): void;
  (e: 'change', value: StaffItem[]): void;
  (e: 'close', value: StaffItem[]): void;
}>();

const staffTabs = [
  { label: '全部', value: 'all' },
  { label: '品牌', value: 'brand' },
  { label: '门店', value: 'shop' },
];

const visible = ref(false);
const activeStaffType = ref('all');
const keyword = ref('');
const optionList = ref<StaffItem[]>([]);
const pageNum = ref(1);
const total = ref(0);
const loading = ref(false);
const noMore = ref(false);
let keywordTimer: ReturnType<typeof setTimeout> | undefined;

const selectedList = computed<StaffItem[]>(() => normalizeModelValue(props.modelValue));
const displayText = computed(() =>
  selectedList.value
    .map((item) => item.name || item.userName || item.id)
    .filter(Boolean)
    .join('、'));

function normalizeModelValue(value: typeof props.modelValue): StaffItem[] {
  if (value === null || value === undefined || value === '') return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map((item: any) => {
    if (typeof item === 'object' && item !== null) {
      const id = item[props.idKey] ?? item.userId ?? item.id ?? item.value;
      const name = item[props.nameKey] ?? item.name ?? item.label ?? item.nickName ?? item.userName ?? item.phonenumber ?? String(id || '');
      return { ...item, id, name };
    }
    return { id: item, name: String(item) };
  });
}

function handleShow() {
  nextTick(() => {
    if (!optionList.value.length) {
      searchOptions();
    }
  });
}

function handleHide() {
  emits('close', selectedList.value);
}

function switchStaffType(type: string) {
  if (activeStaffType.value === type) return;
  activeStaffType.value = type;
  keyword.value = '';
  searchOptions();
}

function handleKeywordInput() {
  if (keywordTimer) clearTimeout(keywordTimer);
  keywordTimer = setTimeout(() => {
    searchOptions();
  }, 300);
}

function buildQuery() {
  const query: Record<string, any> = {
    pageNum: pageNum.value,
    pageSize: props.pageSize,
    reasonable: true,
    staffType: activeStaffType.value,
    ...props.linkParams,
  };
  const keywordValue = keyword.value.trim();
  if (keywordValue) {
    query.keyword = keywordValue;
  }
  return query;
}

async function searchOptions() {
  pageNum.value = 1;
  optionList.value = [];
  noMore.value = false;
  await loadOptions();
}

async function loadNextPage() {
  if (loading.value || noMore.value) return;
  pageNum.value += 1;
  await loadOptions();
}

async function loadOptions() {
  if (loading.value) return;
  loading.value = true;
  try {
    const query = buildQuery();
    const res: any = await request({
      url: '/b/common/staff/options',
      method: 'post',
      params: {
        pageNum: query.pageNum,
        pageSize: query.pageSize,
        reasonable: query.reasonable,
      },
      data: query,
      headers: { repeatSubmit: false },
    });
    const records = res?.records || res?.data?.records || res?.rows || [];
    total.value = Number(res?.total || res?.data?.total || records.length || 0);
    const nextList = records.map(normalizeOption).filter((item: StaffItem) => item.id !== undefined && item.id !== null);
    optionList.value = pageNum.value === 1 ? nextList : optionList.value.concat(nextList);
    noMore.value = optionList.value.length >= total.value || nextList.length < props.pageSize;
  } finally {
    loading.value = false;
  }
}

function normalizeOption(item: any): StaffItem {
  const id = item?.[props.idKey] ?? item?.userId ?? item?.id ?? item?.value;
  const name = item?.[props.nameKey] ?? item?.nickName ?? item?.userName ?? item?.phonenumber ?? String(id || '');
  return {
    ...item,
    id,
    name,
    value: id,
    raw: item,
  };
}

function isSameOption(a: StaffItem, b: StaffItem) {
  return String(a.id) === String(b.id);
}

function isSelected(item: StaffItem) {
  return selectedList.value.some((selected) => isSameOption(selected, item));
}

function toggleOption(item: StaffItem) {
  let nextValue: StaffItem[] = [];
  if (props.multiple) {
    nextValue = isSelected(item) ? selectedList.value.filter((selected) => !isSameOption(selected, item)) : selectedList.value.concat(item);
  } else {
    nextValue = isSelected(item) ? [] : [item];
    visible.value = false;
  }
  emits('update:modelValue', nextValue);
  emits('change', nextValue);
}

function clear() {
  emits('update:modelValue', []);
  emits('change', []);
}
</script>

<style scoped lang="scss">
.advanced-staff-select {
  display: inline-block;

  :deep(.el-input__wrapper) {
    grid-template-columns: max-content minmax(0, 1fr) 16px;
    column-gap: 8px;
  }

  :deep(.el-input__prefix),
  :deep(.el-input__prefix-inner) {
    flex-shrink: 0;
  }

  :deep(.el-input__inner) {
    min-width: 0;
    cursor: pointer;
  }

  :deep(.el-input__suffix),
  :deep(.el-input__suffix-inner) {
    width: 16px;
    min-width: 16px;
    justify-content: center;
  }

  &.custom-picker {
    :deep(.el-input__wrapper) {
      padding-right: 35px !important;
    }

    :deep(.el-input__clear) {
      display: none;
    }
  }
}

:global(.staff-select-popper.el-popover) {
  padding: 0 !important;
  border: 1px solid #ebeef5 !important;
  border-radius: 4px !important;
  box-shadow: 0 8px 24px rgb(0 0 0 / 10%) !important;
}

.staff-select-panel {
  overflow: hidden;
  background: #fff;
  border-radius: 4px;

  .staff-tabs {
    display: flex;
    padding: 12px 12px 0;
    background: #fff;
    border-bottom: 1px solid #ebeef5;
  }

  .staff-tab {
    flex: 1;
    height: 32px;
    border: 0;
    border-radius: 3px 3px 0 0;
    background: transparent;
    color: #606266;
    font-size: 14px;
    cursor: pointer;
    transition:
      color 0.2s,
      background-color 0.2s;

    &:hover {
      color: var(--el-color-primary);
    }

    &.active {
      color: var(--el-color-primary);
      background: #f3edff;
      font-weight: 500;
    }
  }

  .staff-search-wrap {
    padding: 10px 0 6px;
  }

  .staff-search {
    display: block;
    width: 95%;
    margin: 4px auto 0;

    :deep(.el-input__wrapper) {
      width: 100%;
      min-height: 32px;
      border-radius: 4px;
      box-shadow: 0 0 0 1px #dcdfe6 inset;
    }
  }

  .staff-list {
    max-height: 260px;
    padding: 2px 8px 10px;
    overflow-y: auto;
  }

  .staff-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 42px;
    padding: 6px 12px;
    border-radius: 4px;
    color: #303133;
    cursor: pointer;
    transition:
      color 0.2s,
      background-color 0.2s;

    &:hover,
    &.selected {
      color: var(--el-color-primary);
      background: #f5f0ff;
    }
  }

  .staff-info {
    min-width: 0;
  }

  .staff-name {
    overflow: hidden;
    font-size: 14px;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .staff-extra {
    display: flex;
    gap: 8px;
    overflow: hidden;
    color: #909399;
    font-size: 12px;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .staff-check {
    flex-shrink: 0;
    margin-left: 8px;
  }

  .staff-state {
    padding: 8px 0;
    color: #909399;
    font-size: 12px;
    text-align: center;
  }
}
</style>
